import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Upload, Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const FIELDS = [['title', 'Project title *'], ['demo_url', 'Live demo URL'], ['github_url', 'GitHub URL'], ['video_url', 'Video URL']];
const inp = 'w-full px-3 py-2 border border-[#E4E4E7] rounded text-sm text-[#0A0A0A] focus:outline-none focus:border-[#002FA7]';

// Shared by team members (submit) and organizers (edit); saveUrl + headers decide who saves
export const ProjectForm = ({ initial, saveUrl, uploadUrl, headers, onSaved, testPrefix = 'project' }) => {
  const [p, setP] = useState({ title: '', description: '', demo_url: '', github_url: '', video_url: '', image_url: '', ...(initial || {}) });
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);

  const upload = async (file) => {
    if (!file || !uploadUrl) return;
    const fd = new FormData(); fd.append('file', file);
    setUploading(true);
    try {
      const r = await axios.post(uploadUrl, fd, { headers: { ...headers, 'Content-Type': 'multipart/form-data' } });
      setP((x) => ({ ...x, image_url: r.data.url }));
    } catch (e) { toast.error(e.response?.data?.detail || 'Upload failed'); }
    finally { setUploading(false); }
  };

  const save = async () => {
    setBusy(true);
    try {
      const r = await axios.put(saveUrl, p, { headers });
      toast.success(r.data.message || 'Saved'); onSaved?.(r.data.project);
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not save project'); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-3" data-testid={`${testPrefix}-form`}>
      <div className="grid sm:grid-cols-2 gap-3">
        {FIELDS.map(([k, label]) => (
          <div key={k}>
            <label className="block text-[11px] font-bold text-[#A1A1AA] uppercase tracking-[0.08em] mb-1">{label}</label>
            <input value={p[k]} onChange={(e) => setP({ ...p, [k]: e.target.value })} className={inp} data-testid={`${testPrefix}-${k}-input`} />
          </div>
        ))}
      </div>
      <div>
        <label className="block text-[11px] font-bold text-[#A1A1AA] uppercase tracking-[0.08em] mb-1">What did you build?</label>
        <textarea rows={3} value={p.description} onChange={(e) => setP({ ...p, description: e.target.value })} className={inp} data-testid={`${testPrefix}-description-input`} />
      </div>
      <div className="flex items-center gap-3 flex-wrap">
        {p.image_url && <img src={p.image_url.startsWith('/api') ? `${API}${p.image_url}` : p.image_url} alt="" className="w-20 h-14 object-cover rounded border" data-testid={`${testPrefix}-image-preview`} />}
        {uploadUrl && (
          <label className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#002FA7] cursor-pointer" data-testid={`${testPrefix}-image-upload`}>
            {uploading ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />} {p.image_url ? 'Change cover image' : 'Upload cover image'}
            <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={(e) => upload(e.target.files?.[0])} />
          </label>
        )}
        <button onClick={save} disabled={busy || uploading} className="ml-auto px-4 py-2 bg-[#002FA7] hover:bg-[#00227A] text-white rounded text-sm font-semibold disabled:opacity-50" data-testid={`${testPrefix}-save-btn`}>
          {busy ? 'Saving…' : 'Save project'}
        </button>
      </div>
    </div>
  );
};
